import React, { createContext, useContext, useEffect, useState } from 'react';
import ApiService from 'services/apiService';
import { StudentDashboardData, StudentByGradeRow } from '../types';
import type { LkGradeLevel } from 'generated/openApi';

interface StudentDataContextValue {
  data: StudentDashboardData | null;
  loading: boolean;
  error: string | null;
}

const StudentDataContext = createContext<StudentDataContextValue | undefined>(undefined);

interface StudentDataProviderProps {
  children: React.ReactNode;
  schoolYear: number;
}

export const StudentDataProvider: React.FC<StudentDataProviderProps> = ({ 
  children,
  schoolYear
}) => {
  const [data, setData] = useState<StudentDashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch grade levels from the student resource
        const resource = await ApiService.students.getFilterResource();
        const gradeLevelsData: LkGradeLevel[] = resource.gradeLevels ?? [];
        const validGradeLevelIds = new Set(
          gradeLevelsData.map((gl) => gl.id).filter((id): id is number => typeof id === 'number')
        );

        // Get all enrollments for selected school year
        const enrollments = await ApiService.studentEnrollments.getList({
          enrollmentFilter: {
            schoolYears: [schoolYear]
          },
          page: 1,
          pageSize: 10000
        }).catch(() => []);

        // Extract unique grade levels from actual enrollments
        const enrolledGradeLevels = new Set<number>();
        const gradeLevelCounts = new Map<number, Set<number>>();

        enrollments.forEach((enrollment: any) => {
          const gradeLevelStr = enrollment.gradeLevel || enrollment.gradeLevelName;
          const gradeNum = parseInt(gradeLevelStr);
          
          if (!isNaN(gradeNum) && validGradeLevelIds.has(gradeNum) && enrollment.studentId) {
            enrolledGradeLevels.add(gradeNum);
            
            if (!gradeLevelCounts.has(gradeNum)) {
              gradeLevelCounts.set(gradeNum, new Set());
            }
            gradeLevelCounts.get(gradeNum)?.add(enrollment.studentId);
          }
        });

        // Sort grade levels
        const gradeLevels = Array.from(enrolledGradeLevels).sort((a, b) => a - b);

        if (gradeLevels.length === 0) {
          console.warn('No grade levels with enrollments found for school year', schoolYear);
          setData({
            rows: [],
            total: 0,
            schoolYear: `${schoolYear}-${(schoolYear + 1).toString().slice(2)}`,
          });
          setLoading(false);
          return;
        }

        const rows: StudentByGradeRow[] = gradeLevels.map((grade) => ({
          grade,
          count: gradeLevelCounts.get(grade)?.size || 0,
        }));

        const total = Array.from(gradeLevelCounts.values())
          .reduce((sum, studentSet) => sum + studentSet.size, 0);

        setData({
          rows,
          total,
          schoolYear: `${schoolYear}-${(schoolYear + 1).toString().slice(2)}`,
        });
      } catch (err) {
        console.error('Failed to fetch student dashboard data:', err);
        setError('Failed to load student data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [schoolYear]); //Refetch when schoolYear changes

  return (
    <StudentDataContext.Provider value={{ data, loading, error }}>
      {children}
    </StudentDataContext.Provider>
  );
};

export const useStudentData = (): StudentDataContextValue => {
  const context = useContext(StudentDataContext);
  if (!context) {
    throw new Error('useStudentData must be used within StudentDataProvider');
  }
  return context;
};